/**
 * The Observability contract, and the two reads that fetch it. This file names
 * no platform on purpose: it is typechecked in the Workers project and in the
 * browser project, whose type libraries exclude each other. See OBS_HANDOFF.md.
 */

export type ReaderSource = "debrief" | "jd_target";
export type ModelSource = "debrief" | "jd_target" | "mock_reading" | "mock_session";

export type EventRow = {
  readonly eventId: string;
  readonly context: string;
  readonly type: string;
  readonly subject: string;
  readonly actor: string;
  readonly occurredAt: number;
  readonly published: boolean;
};

export type Observability = {
  readonly ok: true;
  readonly readAt: number;
  readonly environment: string;
  readonly pollIntervalMs: number;
  /** How many days the signup series covers, and the windows on offer. */
  readonly windowDays: number;
  readonly windows: readonly number[];
  readonly people: { total: number; newIn30Days: number; seenIn7Days: number };
  readonly pipeline: {
    applications: number;
    active: number;
    closed: number;
    rounds: number;
    roundsSat: number;
    roundsUpcoming: number;
  };
  readonly writeUps: {
    written: number;
    drafts: number;
    read: number;
    awaiting: number;
    observations: number;
    skillsTouched: number;
  };
  readonly practice: {
    sessions: number;
    sessionsEnded: number;
    readings: number;
    jdTargets: number;
    jdTargetsRead: number;
  };
  readonly models: {
    calls: number;
    pins: readonly { pin: string; calls: number }[];
    bySource: readonly { source: ModelSource; calls: number }[];
  };
  readonly reach: {
    signedUp: number;
    tracked: number;
    satARound: number;
    ranAMock: number;
    wroteUp: number;
  };
  readonly signups: readonly { day: string; count: number }[];
  readonly reader: {
    stuck: number;
    retried: number;
    attempts: number;
    worst: number;
    queues: readonly { source: ReaderSource; stuck: number; retried: number; worst: number }[];
    histogram: readonly { attempts: number; stuck: number }[];
  };
  readonly events: { total: number; unpublished: number; recent: readonly EventRow[] };
};

export type ReadingFailure = {
  readonly source: ReaderSource;
  readonly id: string;
  readonly userId: string;
  readonly email: string;
  readonly attempts: number;
  readonly at: number;
};

export type ReadingFailures = {
  readonly ok: true;
  readonly readAt: number;
  readonly audited: true;
  readonly rows: readonly ReadingFailure[];
};

export const NOT_AN_ADMIN = "not_an_admin";
export const UNREACHABLE = "unreachable";

export class AdminApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "AdminApiError";
    this.status = status;
  }
}

// No `credentials`: same origin is the default, and naming it needs the DOM lib.
async function get<T>(path: string): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, { headers: { accept: "application/json" } });
  } catch {
    throw new AdminApiError(0, UNREACHABLE);
  }

  const parsed = (await response.json().catch(() => null)) as
    | (T & { ok?: boolean; error?: string })
    | null;

  if (!response.ok || parsed === null || parsed.ok === false) {
    throw new AdminApiError(response.status, parsed?.error ?? `http_${response.status}`);
  }
  return parsed;
}

export function observabilityPath(days: number | null): string {
  return days === null ? "/api/observability" : `/api/observability?days=${days}`;
}

export function getObservability(days: number | null = null): Promise<Observability> {
  return get<Observability>(observabilityPath(days));
}

/** Names candidates, and writes one `admin_audit` row per person named. */
export function getReadingFailures(): Promise<ReadingFailures> {
  return get<ReadingFailures>("/api/observability?detail=reading_failures");
}
